import React, { useCallback, useState } from 'react';
import { useTranslate } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { showNotification } from 'react-admin';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import { CircularProgress, TextField } from '@material-ui/core';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { renameFileSystemNode } from '../../../../../services/file-explorer/fileExplorerApi';
import { useQueryClient } from 'react-query';

const useStyles = makeStyles(
{
    fullWidth: {
        width: '100%',
    },
});

function RenameFileSystemNodeDialog(props) {
    const { open, fileSystemNodePath, handleCancel, showNotification } = props;

    const classes = useStyles();
    const translate = useTranslate();
    const queryClient = useQueryClient();

    const [isLoading, setIsLoading] = useState(false);
    
    const name = fileSystemNodePath.length > 0 ? fileSystemNodePath[fileSystemNodePath.length - 1] : '';
    const defaultValues = {
        name: name
    };
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(yup.object().shape({
            name: yup.string()
                .required(translate(`cmss.resources.file-explorer.directory-actions-menu.rename-file-system-node.name-is-required`))
                .max(255, translate(`cmss.resources.file-explorer.directory-actions-menu.rename-file-system-node.name-length-cannot-exceed-maximum`).replace('{max}', 255)),
        })),
        defaultValues: defaultValues,
        criteriaMode: 'all',
    });

    const startCreateNewDirectory = useCallback((data) => {
        if (isLoading) {
            return;
        }
        setIsLoading(true);
        const cancellationTokenSource = axios.CancelToken.source();
        renameFileSystemNode(fileSystemNodePath, data.name, cancellationTokenSource.token)
        .then((response) => {
            handleCancelInternal();
            queryClient.invalidateQueries(['/file-explorer/search', 'getDirectoryContent']);
            setIsLoading(false);
            showNotification(`cmss.resources.file-explorer.directory-actions-menu.rename-file-system-node.file-system-node-rename-successful`, 'success');
        }).catch((error) => {
            showNotification(`cmss.resources.file-explorer.directory-actions-menu.rename-file-system-node.file-system-node-rename-unsuccessful`, 'warning');
            setIsLoading(false);
            if (axios.isCancel(error)) {
                return;
            }
        });
    
        return () => {
            cancellationTokenSource.cancel();
        }
    }, [isLoading, fileSystemNodePath, queryClient]);


    function resetDialog() {
        reset(defaultValues);
        setIsLoading(false);
    }

    function handleOnClickCreateDialogButton(data) {
        startCreateNewDirectory(data);
    }

    function handleCancelInternal() {
        handleCancel();
    }

    return (
        <Dialog open={open} TransitionProps={{onEnter: resetDialog, onClose: handleCancelInternal}} fullWidth={true} maxWidth="sm">
            <DialogTitle>{translate(`cmss.resources.file-explorer.directory-actions-menu.buttons.rename`)} <small>({name})</small></DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormControl className={classes.fullWidth} variant="outlined">
                            <TextField
                                variant="outlined"
                                type="text"
                                label={translate(`cmss.resources.file-explorer.directory-actions-menu.rename-file-system-node.name`)}
                                {...register('name')}
                                error={errors.name ? true : false}
                                helperText={errors.name?.message ?? undefined}
                                defaultValue={name}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                variant="contained"
                disabled={isLoading}
                onClick={handleSubmit(handleOnClickCreateDialogButton)}
                color="primary"
                >
                    {translate(`cmss.resources.file-explorer.directory-actions-menu.rename-file-system-node.rename`)}
                    {isLoading && (
                        <CircularProgress
                            size={24}
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                marginTop: '-12px',
                                marginLeft: '-12px',
                            }}
                        />
                    )}
                </Button>
                <Button onClick={handleCancelInternal} color="secondary">
                    {translate(`cmss.resources.file-explorer.directory-actions-menu.rename-file-system-node.cancel`)}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default connect(null, {
    showNotification
})(RenameFileSystemNodeDialog);